import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

// Smart API URL construction: if base URL already has /api, use it as-is, otherwise add /api
const getApiBaseUrl = () => {
  return API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for cross-origin requests
})

// Add request interceptor to automatically include auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('evop_toolkit_auth_token')
    console.log('🔍 API interceptor - token available:', !!token)
    console.log('🔍 API interceptor - request URL:', config.url)
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      console.log('🔍 API interceptor - added Authorization header')
    } else {
      console.log('⚠️ API interceptor - no token available')
    }
    
    return config
  },
  (error) => {
    console.error('❌ API interceptor request error:', error)
    return Promise.reject(error)
  }
)

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log('📊 API response:', response.status, response.config.url)
    return response
  },
  (error) => {
    console.log('❌ API response error:', error.response?.status, error.config?.url)
    
    if (error.response?.status === 401) {
      console.log('🧹 401 error - clearing invalid token')
      // Clear invalid token and redirect to login
      localStorage.removeItem('evop_toolkit_auth_token')
      // Don't redirect here, let the auth context handle it
    }
    return Promise.reject(error)
  }
)

// Secret Management
export const secretsApi = {
  list: () => api.get('/secrets/list'),
  listLightspeed: () => api.get('/secrets/lightspeed'),
  listSkimmer: () => api.get('/secrets/skimmer'),
  testConnection: (secretName: string) => 
    api.post('/secrets/test-connection', null, { params: { secret_name: secretName } }),
}

// Lightspeed Exports
export const lightspeedApi = {
  getProductFields: () => api.get('/lightspeed/products/fields'),

  getSalesFields: () => api.get('/lightspeed/sales/fields'),

  getCustomerFields: () => api.get('/lightspeed/customers/fields'),

  getSalesCacheStatus: (fpCode: string) => api.get(`/lightspeed/sales/cache-status?fp_code=${fpCode}`),

  clearSalesCache: (fpCode: string) => api.delete(`/lightspeed/sales/cache?fp_code=${fpCode}`),

  getStatus: (fpCode: string) =>
    api.get('/lightspeed/status', { params: { fp_code: fpCode } }),
}

// Helper function to download CSV files
export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Helper to extract filename from response headers
export const getFilenameFromResponse = (response: any): string => {
  const contentDisposition = response.headers['content-disposition']
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/)
    if (match) {
      return match[1]
    }
  }
  return 'export.csv'
}

export default api